/**
 * Every behavioural literal the TS surface owns, per the repo convention:
 * named, commented with provenance, exactly one owner per constant. Tuned CV
 * thresholds live behind the castvision seam (its constants module), never here.
 */

/** How often an idle worker polls for a claimable job. */
export const WORKER_POLL_MS = 2_000;

/** How often a running job's on-disk manifest is re-read to advance state. */
export const MANIFEST_POLL_MS = 1_000;

/** Upload cap for selfies. */
export const MAX_PORTRAIT_BYTES = 10 * 1024 * 1024;

/** Matches the proven smoke upload boundary for a short generic source GIF. */
export const MAX_GIFME_SOURCE_BYTES = 100 * 1024 * 1024;

/** Bounded multipart headers, boundaries, filenames, and scalar fields. */
export const GIFME_MULTIPART_OVERHEAD_BYTES = 2 * 1024 * 1024;

/** One maximum portrait and bounded multipart overhead for immediate selfie saves. */
export const MAX_GIFME_SELFIE_REQUEST_BYTES = MAX_PORTRAIT_BYTES + GIFME_MULTIPART_OVERHEAD_BYTES;

/** One maximum portrait, one maximum source GIF, and multipart overhead. */
export const MAX_GIFME_SINGLE_REQUEST_BYTES =
  MAX_PORTRAIT_BYTES + MAX_GIFME_SOURCE_BYTES + GIFME_MULTIPART_OVERHEAD_BYTES;

/**
 * Accepted portrait uploads. The authority is the legacy CLI's IMAGE_SUFFIXES
 * (gif-me `pipeline.py`); this gate exists so a bad upload fails at POST time
 * instead of wasting a job claim.
 */
export const PORTRAIT_SUFFIXES = new Set([".jpg", ".jpeg", ".png", ".webp"]);

/** Subdirectories of the data dir. */
export const JOBS_SUBDIR = "jobs";
export const GIFME_JOB_LOG_NAME = "stderr.log";
export const GIFME_SUBDIR = "gifme";
export const GIFME_STAGING_SUBDIR = "staging";
export const GIFME_USERS_SUBDIR = "users";
export const GIFME_RUNS_SUBDIR = "runs";
export const GIFME_DIAGNOSTICS_SUBDIR = "diagnostics";
export const GIFME_SELFIES_SUBDIR = "selfies";
export const GIFME_SOURCES_SUBDIR = "sources";
export const GIFME_SOURCE_PREVIEWS_SUBDIR = "source-previews";
export const GIFME_SOURCE_PREPROCESS_SUBDIR = "source-preprocess";

/** Filesystem bundle contract for one exact provider-ready source input. */
export const SOURCE_PREPROCESS_CACHE_CONTRACT = 1;

/** Invalidate when the blur/previs recipe changes without changing its inputs. */
export const SOURCE_PREPROCESS_RECIPE_VERSION = 1;

/** Invalidate when normalizeForOmni changes its emitted canvas semantics. */
export const NORMALIZE_FOR_OMNI_CONTRACT_VERSION = 1;

/** Canonical names inside every successful GIF Me run directory. */
export const GIFME_SOURCE_NAME = "source.gif";
export const GIFME_OUTPUT_NAME = "output.gif";
export const GIFME_MP4_NAME = "output.mp4";
export const GIFME_SUBMITTED_INPUT_NAME = "submitted-input.mp4";
export const GIFME_PROVIDER_PRECONFORM_NAME = "provider-pre-conform.mp4";
export const GIFME_SOURCE_PREVIEW_NAME = "source-preview.webp";

/** Bound an authenticated on-demand picker thumbnail transform. */
export const GIFME_PREVIEW_TIMEOUT_MS = 30_000;

/** Web-process ceiling for simultaneous ffmpeg thumbnail transforms. */
export const MAX_GIFME_PREVIEW_PROCESSES = 2;

/** Web-process ceiling for simultaneous public/authenticated rendition hashes. */
export const MAX_GIFME_RENDITION_VALIDATIONS = 2;

/** Web-process ceiling for distinct rendition hashes waiting for an active slot. */
export const MAX_GIFME_RENDITION_VALIDATION_WAITERS = 8;

/** A queued rendition hash fails closed rather than waiting indefinitely. */
export const GIFME_RENDITION_VALIDATION_WAIT_TIMEOUT_MS = 5_000;

/** Small stat-keyed verdict cache; media bodies are never retained here. */
export const MAX_GIFME_RENDITION_VALIDATION_CACHE_ENTRIES = 512;

/** Bound each descriptor-backed response read and its temporary allocation. */
export const GIFME_RENDITION_STREAM_CHUNK_BYTES = 64 * 1024;

/**
 * Unknown staging directories survive one day so an upload interrupted between
 * its disk write and database transaction can finish or be retried safely.
 */
export const GIFME_STAGING_MAX_AGE_MS = 24 * 60 * 60 * 1_000;

/** Config defaults, overridable via env (see `src/config.ts`). */
export const DEFAULT_PORT = 3000;
export const DEFAULT_DATA_DIR = "data";

/** Cap on client-chosen idempotency keys, to keep the unique index sane. */
export const MAX_IDEMPOTENCY_KEY_CHARS = 200;

/** Artifact filenames. The gif/mp4 names double as the CLI --output names. */
export const OUTPUT_GIF_NAME = "out.gif";
export const OUTPUT_MP4_NAME = "out.mp4";
export const CLI_WORK_SUBDIR = "out.work";

/**
 * Where the legacy CLI writes its manifest: `<output>.manifest.json` next to
 * the output (gif-me `pipeline.py` `CastRequest.manifest_path`).
 */
export const CLI_MANIFEST_NAME = `${OUTPUT_GIF_NAME}.manifest.json`;

/** castvision contract version this repo speaks (design note 2026-08-18). */
export const CASTVISION_CONTRACT = "1";

/** Contract exit code for a malformed/unknown-field request. */
export const CASTVISION_EXIT_MALFORMED = 64;

/** Per-op kill ceiling for sidecar calls — pixel work, never network. */
export const CASTVISION_OP_TIMEOUT_MS = 5 * 60_000;

/** Disclosure transform timeout; kept explicit for job-ceiling accounting. */
export const DISCLOSURE_OP_TIMEOUT_MS = CASTVISION_OP_TIMEOUT_MS;

/** Output suffix the harness gives each op's `out` (legacy writes by suffix). */
export const CASTVISION_OP_OUT_SUFFIX: Record<string, string> = {
  disclose: ".mp4",
  "identity-crop": ".png",
  anonymize: ".mp4",
  look: ".mp4",
  loop: ".gif",
};

/** Parity harness corpus locations (design note: parity-harness). */
export const CORPUS_PATH = "corpus/corpus.json";
export const GOLDEN_DIR = "corpus/golden";

/**
 * Verdict-drift tolerances for the parity compare. Gate agreement is carried
 * by `applied` (exact); these only bound numeric drift between runtimes.
 */
export const PARITY_NUMERIC_TOLERANCE = 0.02;
export const PARITY_FPS_TOLERANCE = 0.5;
export const PARITY_MB_TOLERANCE = 0.5;
export const PARITY_WRAP_TOLERANCE = 2.0;

/** Per-op field tolerances and ignored (implementation-prose) fields. */
export const PARITY_TOLERANCES: Record<string, Record<string, number>> = {
  anonymize: {
    coverage: PARITY_NUMERIC_TOLERANCE,
    detail_residual: PARITY_NUMERIC_TOLERANCE,
  },
  look: { fps: PARITY_FPS_TOLERANCE },
  loop: {
    fps: PARITY_FPS_TOLERANCE,
    megabytes: PARITY_MB_TOLERANCE,
    wrap_difference: PARITY_WRAP_TOLERANCE,
  },
};
export const PARITY_IGNORED_FIELDS: Record<string, string[]> = {
  anonymize: ["reason"],
};

/**
 * MediaPipe face landmarker bundle. Provenance: legacy gif-me
 * `preprocess.py`. The ORCHESTRATOR provisions this into `models/`
 * (`bun run models:fetch`); the sidecar never downloads.
 */
export const MODELS_DIR = "models";
export const LANDMARKER_MODEL_NAME = "face_landmarker.task";
export const LANDMARKER_MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";

/**
 * Model canvas (provenance: legacy `preprocess.py`). Gemini Omni edits on a
 * 720p canvas; sources shorter than the floor are looped up to it because
 * the provider processes under-length references unreliably.
 */
export const CANVAS_FLOOR_SECONDS = 3.5;

/**
 * Post-production trims the generation back to one pass of the source beat
 * (the loop-up to CANVAS_FLOOR_SECONDS replays it). The generation rarely
 * lands on the source duration to the millisecond, so a trim is only worth
 * doing when materially more than one beat is present. Provenance: gif-me
 * `previs-over-blur` (8b56d2c).
 */
export const TRIM_TOLERANCE_SECONDS = 0.08;
export const MAX_CANVAS_SECONDS = 10.0;
export const CANVAS_PORTRAIT: [number, number] = [720, 1280];
export const CANVAS_LANDSCAPE: [number, number] = [1280, 720];
export const MODEL_CANVAS_FPS = 24;

/**
 * Packed-canvas ceiling. The packed mode sends each source frame exactly once
 * at MODEL_CANVAS_FPS, so the canvas is just the frame count divided by the
 * cadence — this caps how long that is allowed to get, and therefore what a
 * single generation can ever cost. Three seconds is a product choice, not a
 * provider limit: it is long enough to carry a reaction beat and short enough
 * that the worst-case bill is bounded. Provenance: measured against the
 * testinput set, where every clip either fits inside it or is a long tail we
 * would rather truncate in frames than pay for in seconds.
 */
export const PACKED_CANVAS_MAX_SECONDS = 3.0;

/**
 * Frames a packed canvas may carry. Derived so it cannot drift out of step
 * with the cap or the cadence; a clip at or under this loses nothing at all.
 */
export const PACKED_CANVAS_MAX_FRAMES = Math.round(PACKED_CANVAS_MAX_SECONDS * MODEL_CANVAS_FPS);
export const BACKDROP_BLUR_SIGMA = 28;

/** Encode parameters (provenance: legacy `preprocess.py` / `post/`). */
export const X264_PRESET = "medium";
export const NORMALIZE_CRF = 19;
export const CONFORM_CRF = 18;
export const POST_CRF = 18;

/** Detail bottleneck (provenance: legacy `post/detail.py`). */
export const DETAIL_COLORS = 256;
export const DETAIL_BAYER_SCALE = 3;
export const DETAIL_DOWNSCALE_KERNEL = "area";
export const DETAIL_UPSCALE_KERNEL = "bilinear";

/** Aspect ratios closer than this are treated as already matching. */
export const ASPECT_MATCH_EPSILON = 0.01;

/** Longest ffmpeg stderr tail carried into an error message. */
export const FFMPEG_STDERR_TAIL_CHARS = 800;

/** Source clip gates (provenance: legacy `pipeline.py`). */
export const MIN_SOURCE_SECONDS = 0.5;
export const MAX_SOURCE_SECONDS = MAX_CANVAS_SECONDS;

/**
 * Gemini Omni provider surface (provenance: legacy `omni.py`). The base URL
 * is overridable via GEMINI_API_BASE for the fake-provider test seam only.
 */
export const GEMINI_API_BASE = "https://generativelanguage.googleapis.com";
export const FILES_PATH = "/v1beta/files";
export const OMNI_REQUEST_TIMEOUT_MS = 900_000;
export const OMNI_DOWNLOAD_TIMEOUT_MS = 300_000;
export const OMNI_FILE_WAIT_TIMEOUT_MS = 900_000;
export const OMNI_FILE_POLL_MS = 5_000;

/**
 * Blurred submissions per run, escalation included. Three is two escalations
 * past the mask that was asked for, which is where the ladder stops being an
 * experiment and starts being a way to spend attempts on footage that is
 * refused on the footage.
 */
export const MAX_BLUR_ATTEMPTS = 3;

/**
 * Paid submissions a single job may make: the initial unblurred edit plus every
 * rung of the blur ladder.
 *
 * "One paid generation per job" SURVIVES this being larger than 2. Every
 * submission past the first happens only because the previous one was refused
 * BEFORE any video was generated — a likeness refusal is input-blocked, so it
 * costs input tokens and nothing else. At most one submission per job ever
 * produces billable video, which is the invariant that matters; this number
 * bounds attempts, not generations.
 *
 * Derived from the ladder rather than pinned, so it cannot drift out of step
 * with it. Used only to size the job kill ceiling below — the pipeline bounds
 * itself on MAX_BLUR_ATTEMPTS.
 */
export const MAX_PROVIDER_SUBMISSIONS_PER_JOB = 1 + MAX_BLUR_ATTEMPTS;

/**
 * Post-production allowance after the provider stages: audio strip, aspect
 * conform, castvision look/loop, gif encode. Generous against corpus runs,
 * which finish post-production in well under a minute.
 */
export const BASE_POST_PRODUCTION_HEADROOM_MS = 5 * 60_000;

export const POST_PRODUCTION_HEADROOM_MS =
  BASE_POST_PRODUCTION_HEADROOM_MS + DISCLOSURE_OP_TIMEOUT_MS;

/**
 * Kill the CLI after this long. Derived, never tuned independently: the
 * worst provider path the CLI's own stage timeouts allow
 * (`MAX_PROVIDER_SUBMISSIONS_PER_JOB` request cycles, then the generated-file
 * wait and download) plus post-production headroom. tests/constants.test.ts
 * forbids this ceiling from regressing below the stages it must contain.
 */
export const JOB_TIMEOUT_MS =
  MAX_PROVIDER_SUBMISSIONS_PER_JOB * OMNI_REQUEST_TIMEOUT_MS +
  OMNI_FILE_WAIT_TIMEOUT_MS +
  OMNI_DOWNLOAD_TIMEOUT_MS +
  POST_PRODUCTION_HEADROOM_MS;

/**
 * Pricing (provenance: legacy `omni.py`, and the Gemini API pricing page for
 * the 360p rate). Both Omni Flash Preview and Omni 1.1 Flash are listed at the
 * same input and video-output rates, so the per-second token count is the only
 * thing that varies with resolution.
 *
 * The 720p rate is exact against observation: three paid runs billed 5792.0,
 * 5792.0 and 5792.1 output tokens per second of returned video.
 */
export const INPUT_USD_PER_MILLION_TOKENS = 1.5;
export const VIDEO_OUTPUT_USD_PER_MILLION_TOKENS = 17.5;
export const VIDEO_OUTPUT_TOKENS_PER_SECOND_720P = 5_792;
export const VIDEO_OUTPUT_TOKENS_PER_SECOND_360P = 1_931;

/** Redacted provider-error cap (provenance: legacy `pipeline.py`). */
export const PROVIDER_ERROR_MAX_CHARS = 800;

/**
 * Prompts (provenance: legacy `pipeline.py`). Deliberately short — longer
 * prompts measurably caused MORE scene regeneration and directly encouraged
 * body replacement.
 */
export const FACE_PROMPT =
  "Change only the primary performer's facial identity and hair to match the supplied " +
  "image. Copy the source performer's exact facial performance frame by frame: mouth " +
  "shape, eye openness, brows, gaze, head pose, and timing. The supplied image defines " +
  "identity only; do not copy its smile or expression. Preserve the original head size, " +
  "neck, body, clothing, hands, framing, and on-screen text. Keep everything else the " +
  "same. Single continuous shot. No scene cuts.";

export const PERSON_PROMPT =
  "Change the primary performer's identity and appearance to match the supplied image. " +
  "Copy the source performer's exact facial performance, pose, gesture, gaze, head motion, " +
  "and timing. Preserve the source clothing, props, scene, framing, camera, and on-screen " +
  "text. Keep everything else the same. Single continuous shot. No scene cuts.";

/**
 * Appended when the retry runs against a blurred source. The model has to be
 * told the blur is deliberate, or it reproduces a smeared face; and which
 * acting signals were destroyed, because "preserve the source expression" is
 * meaningless once the eyes, brows and mouth are gone.
 */
export const BLURRED_SOURCE_DIRECTIONS = {
  face:
    "The source performer's facial region has been intentionally blurred; their hair, " +
    "head outline and body are untouched. Reconstruct a fully sharp, detailed face there " +
    "from the supplied portrait, at the head size, angle and screen position the blurred " +
    "region occupies. Do not reproduce a blurred, grey or smeared face. Take head pose, " +
    "head motion and timing from the source, and the facial acting from the performance " +
    "description below.",
  eyes:
    "The source performer's eye and brow region has been intentionally covered with a " +
    "flat patch; the rest of the face and everything else is untouched. Reconstruct " +
    "sharp, detailed eyes and brows there from the supplied portrait, at the position " +
    "the patch occupies. Do not reproduce a flat, blurred or smeared eye region. Take " +
    "eye direction and blinking from the performance description below.",
  // The head scope takes the hair with it, so the model has to be asked for hair
  // as well — the face text PROMISES hair is untouched, which would be a lie
  // here and leaves the model reconstructing a face inside a blurred skull.
  head:
    "The source performer's entire head — face, hair, ears and head outline — has been " +
    "intentionally blurred; their body, clothing and everything else is untouched. " +
    "Reconstruct a fully sharp, detailed head there from the supplied portrait, including " +
    "hair and hairline, at the size, angle and screen position the blurred region occupies. " +
    "Do not reproduce a blurred, grey or smeared head. Take head pose, head motion and " +
    "timing from the source, and the facial acting from the performance description below.",
} as const;

/**
 * Used instead of BLURRED_SOURCE_DIRECTIONS when the blurred region carries
 * previs line-art. Two things have to be said that the plain-blur text does
 * not: that the strokes ARE the acting and should be followed frame by
 * frame, and that they must not be drawn into the output — without the
 * second sentence the model faithfully reproduces a wireframe on the face.
 * Text ported verbatim from gif-me previs-over-blur (head b485aee): the
 * eye-shading and gaze reading, and the asymmetry directions, replace the
 * db388bc filled-aperture text for the eye — an eye's opening is bright
 * sclera with a dark iris on it, so the mouth's fill rule inverted it.
 */
export const PREVIS_SOURCE_DIRECTIONS = {
  face:
    "The source performer's facial region has been intentionally blurred, and their " +
    "facial performance has been redrawn inside it as thin line-art tracing the lips, " +
    "eyelids, irises and brows; their hair, head outline and body are untouched. " +
    "Reconstruct a fully sharp, detailed face there from the supplied portrait, at the " +
    "head size, angle and screen position the blurred region occupies. The line-art is " +
    "the acting reference: follow it frame by frame for mouth shape, eye openness, gaze " +
    "and brow position. A filled dark shape at the mouth is the opening between the " +
    "lips: the larger it is, the wider the mouth is open. Each eye is drawn the way an " +
    "eye is actually shaded — a bright almond for the white of the eye, with a dark disc " +
    "on it for the iris. The height of the bright almond is how open that eye is, and " +
    "the position of the dark disc within it is where that eye is looking: disc toward " +
    "the left of the almond means looking left, toward the right means looking right, " +
    "centred means looking straight ahead. A single line with no fill means shut — lips " +
    "together, or lids closed. The two eyes and the two brows are drawn independently " +
    "and are often not doing the same thing: read each side separately and keep any " +
    "asymmetry — a raised brow over a level one, or one eye narrowed while the other " +
    "stays open. Brow height is the gap between the brow line and the eye below it. " +
    "Render a real face, never the drawing itself — no strokes, " +
    "outlines, contour lines, wireframe or dark patches may appear anywhere in the " +
    "output. Take head pose, head motion and timing from the source.",
  eyes:
    "The source performer's eye and brow region has been intentionally covered with a " +
    "flat patch, and their eye performance has been redrawn on it as thin line-art " +
    "tracing the eyelids, irises and brows; the rest of the face and everything else is " +
    "untouched. Reconstruct sharp, detailed eyes and brows there from the supplied " +
    "portrait, at the position the patch occupies. The line-art is the acting reference: " +
    "follow it frame by frame for eye openness, gaze direction and brow position. Each " +
    "eye is drawn the way an eye is actually shaded — a bright almond for the white of " +
    "the eye, with a dark disc on it for the iris. The height of the bright almond is " +
    "how open that eye is, and the position of the dark disc within it is where that eye " +
    "is looking. A single line with no fill means the lids are closed. The two eyes and " +
    "the two brows are drawn independently and are often not doing the same thing: read " +
    "each side separately and keep any asymmetry. Brow height is the gap between the " +
    "brow line and the eye below it. Render real eyes, never the " +
    "drawing itself — no strokes, outlines, contour lines, wireframe or dark patches may " +
    "appear anywhere in the output.",
  // Strictly more blurred area than the face scope means strictly more acting to
  // put back, and the same hair correction as the plain-blur head text.
  head:
    "The source performer's entire head — face, hair, ears and head outline — has been " +
    "intentionally blurred, and their facial performance has been redrawn inside it as thin " +
    "line-art tracing the lips, eyelids, irises and brows; their body and clothing are " +
    "untouched. Reconstruct a fully sharp, detailed head there from the supplied portrait, " +
    "including hair and hairline, at the size, angle and screen position the blurred region " +
    "occupies. The line-art is the acting reference: follow it frame by frame for mouth " +
    "shape, eye openness, gaze and brow position. A filled dark shape at the mouth is the " +
    "opening between the lips: the larger it is, the wider the mouth is open. Each eye is " +
    "drawn the way an eye is actually shaded — a bright almond for the white of the eye, " +
    "with a dark disc on it for the iris. The height of the bright almond is how open that " +
    "eye is, and the position of the dark disc within it is where that eye is looking: disc " +
    "toward the left of the almond means looking left, toward the right means looking " +
    "right, centred means looking straight ahead. A single line with no fill means shut — " +
    "lips together, or lids closed. The two eyes and the two brows are drawn independently " +
    "and are often not doing the same thing: read each side separately and keep any " +
    "asymmetry — a raised brow over a level one, or one eye narrowed while the other stays " +
    "open. Brow height is the gap between the brow line and the eye below it. Render a real " +
    "head, never the drawing itself — no strokes, outlines, contour lines, wireframe or dark " +
    "patches may appear anywhere in the output. Take head pose, head motion and timing from " +
    "the source.",
} as const;

export const IMAGE_SUFFIXES = new Set([".jpg", ".jpeg", ".png", ".webp"]);
export const SOURCE_SUFFIXES = new Set([".gif", ".mp4", ".mov", ".webm"]);
export const OUTPUT_SUFFIXES = new Set([".gif", ".mp4"]);
export const SWAP_MODES = new Set(["face", "person"]);
export const BLUR_SCOPES = new Set(["face", "eyes", "head"]);
/** The blur regions, as a type. `head` is the ladder's top rung. */
export type BlurScope = "face" | "eyes" | "head";
/**
 * What the anonymize op does about a frame the detector never saw. `widen`
 * covers it with the sweep between its neighbours, deliberately oversized;
 * `fail` abandons the run, which is what the coverage gate used to do
 * unconditionally. The sidecar owns the thresholds and the margin sizing —
 * this set exists only so a bad flag is rejected before a subprocess starts.
 */
export const BLUR_FALLBACKS = new Set(["widen", "fail"]);

/**
 * What each blur scope is called in a log line or an error. TS-side prose only:
 * the sidecar's own diagnostics report the pixel facts it alone knows (frames
 * contested, bridged, widened) and never name the region, so this is not a
 * constant duplicated across the seam.
 */
export const BLUR_REGION_NAMES: Record<string, string> = {
  eyes: "eye band",
  face: "facial region",
  head: "whole head",
};

/**
 * The rungs the likeness retry climbs, in order.
 *
 * Growing the mask is the one lever the upstream measurements leave untested:
 * they establish that the eye band is not enough and that completeness beats
 * size, but the only measurement ABOVE the facial region is a historical
 * full-head blur taken before occluded runs were bridged — those runs were
 * leaking frames anyway, so they say nothing about size.
 *
 * `widen` is RELATIVE to the scope's own default hull, which the sidecar owns:
 * 1.0 is that hull, BLUR_LADDER_WIDEN is 15% wider. Upstream passed an absolute
 * hull scale and recovered the percentage by sharing DEFAULT_SHRINK between two
 * modules of one process; here that constant would have to exist in both
 * languages, so the factor crosses the contract instead and this side needs no
 * geometry at all.
 *
 * The ladder is entered at the requested scope, so `--blur-scope` decides where
 * it starts and it never re-tries something smaller than what was asked for.
 * (provenance: gif-me previs-over-blur 24884b2 `BLUR_LADDER`)
 */
export const BLUR_LADDER_WIDEN = 1.15;
export const BLUR_LADDER: readonly { scope: BlurScope; widen: number }[] = [
  { scope: "eyes", widen: 1.0 },
  { scope: "face", widen: 1.0 },
  { scope: "face", widen: BLUR_LADDER_WIDEN },
  { scope: "head", widen: 1.0 },
];

export const LOOK_MODES = new Set(["matched", "codec", "clean"]);
